// services/orderDetailsEdit.ts
//
// Editing an order after it is assigned (Phase 1, client 2026-10-14): "dispatch needs to update the
// number, payment and address without waiting for the driver". Dispatch may change an order's own
// details — customer name, phone, email, address, payment, notes, the photo rule, the Night tag — on
// any order that is still open, whoever holds it. The products and the order type stay as they are
// once a store or driver has the order: changing them moves stock, which is Phase 2. No stock moves
// here, so the save is one conditional write of the order row.
//
// Pure rules, no React / Supabase imports, so the page, the context and the tests share them.
import { OrderStatus, type Order } from '../types';

/** Statuses an order can no longer be edited in: done, cancelled, or on its way back to the store. */
export const DETAILS_LOCKED_STATUSES: readonly OrderStatus[] = [
  OrderStatus.DELIVERED,
  OrderStatus.CANCELLED,
  OrderStatus.RETURNING_TO_STORE,
];

/** May dispatch open this order in the edit form? */
export function canEditOrderDetails(status: OrderStatus | string | undefined): boolean {
  return !!status && !(DETAILS_LOCKED_STATUSES as readonly string[]).includes(status);
}

/** Are the products (and the order type) fixed for this order? Only a Pending order has no store or
 *  driver holding stock for it. */
export function productsLockedFor(status: OrderStatus | string | undefined): boolean {
  return status !== OrderStatus.PENDING;
}

/** The order's own detail fields — what an edit of an assigned order may write. */
export const ORDER_DETAIL_KEYS: ReadonlySet<string> = new Set([
  'customerName', 'customerPhone', 'customerEmail',
  'address', 'formattedAddress', 'addressLat', 'addressLng', 'addressPlaceId', 'addressVerified', 'addressKept',
  'requiresCash', 'cashAmount', 'requiresETransfer', 'eTransferAmount',
  'notes', 'requirePhoto', 'priority', 'isNight',
]);

/** Thrown when an edit of an assigned order would change something only an unassigned order may change. */
export class OrderDetailsRefused extends Error { name = 'OrderDetailsRefused'; }

const blank = (v: unknown): boolean => v === undefined || v === null || v === '' || v === false;

/** Split a computed change set into what may be written and refuse what may not. `changed` is the
 *  diff of the held order (`prev`) against the edited copy — only the fields that differ. A field that
 *  went from missing to empty (notes '' for none, an unticked box) is not a change: writing it would
 *  tell the driver about notes nobody wrote. An order saved before Same Day / Mail existed is Same Day. */
export function detailsOnly(prev: Partial<Order>, changed: Record<string, unknown>): Record<string, unknown> {
  if ('items' in changed) {
    throw new OrderDetailsRefused('The products cannot change while a store or driver has the order. Remove the store or driver first.');
  }
  if ('orderType' in changed && (changed.orderType ?? 'delivery') !== (prev.orderType ?? 'delivery')) {
    throw new OrderDetailsRefused('Same Day / Mail cannot change while a store or driver has the order. Remove the store or driver first.');
  }
  const patch: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(changed)) {
    if (!ORDER_DETAIL_KEYS.has(k)) continue;
    if (blank(v) && blank((prev as Record<string, unknown>)[k])) continue;
    patch[k] = v;
  }
  return patch;
}

const money = (n: unknown): string => `$${(Number(n) || 0).toFixed(2)}`;

/** The text the driver gets when dispatch changed something that matters on the road, or null. One
 *  message, the new values where they fit in a text. */
export function driverEditText(next: Pick<Order, 'id' | 'orderNumber' | 'address' | 'customerName' | 'requiresCash' | 'cashAmount' | 'requiresETransfer' | 'eTransferAmount'>, patch: Record<string, unknown>): string | null {
  const parts: string[] = [];
  if ('address' in patch) parts.push(`new address: ${next.address}`);
  if ('customerName' in patch) parts.push(`customer: ${next.customerName}`);
  if ('customerPhone' in patch) parts.push('new customer phone number');
  if ('cashAmount' in patch || 'requiresCash' in patch || 'eTransferAmount' in patch || 'requiresETransfer' in patch) {
    const pay: string[] = [];
    if (next.requiresCash && (Number(next.cashAmount) || 0) > 0) pay.push(`cash ${money(next.cashAmount)}`);
    if (next.requiresETransfer && (Number(next.eTransferAmount) || 0) > 0) pay.push(`e-transfer ${money(next.eTransferAmount)}`);
    parts.push(`payment: ${pay.length ? pay.join(' + ') : 'none'}`);
  }
  if ('notes' in patch) parts.push('new notes');
  if (parts.length === 0) return null;
  const orderNumber = next.orderNumber || next.id.slice(-6).toUpperCase();
  return `WolfDrivers: Order #${orderNumber} was updated by dispatch — ${parts.join('; ')}. Check your app.`;
}
